import type {
  Asset,
  AssetCollectionPage,
  AssetCollectionResponse,
  AssetKind,
  AssetListStatus,
  AssetPage,
  AssetResponse,
  AssetSort,
  SimilarAssets,
} from "@pax/api-contract/assets/v1alpha1";
import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { useSession } from "@/features/auth/queries";
import { api, publicApi, publicOrpc, reachable } from "@/lib/api";

const PAGE_SIZE = 30;
const MAX_QUERY_LENGTH = 200;

/**
 * Who is looking. Assets carry the reader's own reaction and collections their
 * role, so every read is keyed by the reader: signing in or out reads a fresh
 * entry instead of somebody else's.
 */
export type AssetViewer = string | null;

export function useAssetViewer(): AssetViewer {
  return useSession().data?.user.id ?? null;
}

/** What the library is narrowed to. Everything here is in the address. */
export interface LibraryFilters {
  q?: string;
  kind?: AssetKind;
  sort?: AssetSort;
  tag?: string;
  collection?: string;
  hideSensitive?: boolean;
  humanVerifiedOnly?: boolean;
  excludeAiGenerated?: boolean;
}

export function normalizeAssetQuery(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const query = value.trim().slice(0, MAX_QUERY_LENGTH);
  return query || undefined;
}

const flag = (on: boolean | undefined) => (on ? ("true" as const) : undefined);

/**
 * The library as narrowed: browsed in its sort, or, with words typed, the
 * search's best matches in its own order. Either way it pages as assets.
 */
export const librarySearchQuery = (filters: LibraryFilters, viewer: AssetViewer) =>
  infiniteQueryOptions({
    queryKey: [
      ...(filters.q ? publicOrpc.assets.search.key() : publicOrpc.assets.list.key()),
      { filters, viewer },
    ],
    queryFn: async ({ pageParam }): Promise<AssetPage> => {
      const narrowed = {
        limit: PAGE_SIZE,
        kind: filters.kind,
        withAllTags: filters.tag,
        inAnyCollection: filters.collection,
        includeSensitive: filters.hideSensitive ? ("false" as const) : ("true" as const),
        humanVerifiedOnly: flag(filters.humanVerifiedOnly),
        excludeAiGenerated: flag(filters.excludeAiGenerated),
        cursor: pageParam ?? undefined,
      };
      if (!filters.q) {
        return reachable(publicApi.assets.list({ query: { ...narrowed, sort: filters.sort } }));
      }
      const page = await reachable(
        publicApi.assets.search({ query: { ...narrowed, q: filters.q } }),
      );
      return { assets: page.results.map(result => result.asset), nextCursor: page.nextCursor };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: AssetPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

/**
 * Get an asset's whole response, which carries its editors' moderation state beside the asset: at
 * one of its versions, or as anyone first sees it, at its live version.
 */
const assetResponseQuery = (assetId: string, viewer: AssetViewer, versionId?: string) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.get.key(), { assetId, viewer, versionId }],
    queryFn: () =>
      reachable(publicApi.assets.get({ params: { id: assetId }, query: { versionId } })),
    staleTime: 30_000,
  });

export const assetQuery = (assetId: string, viewer: AssetViewer, versionId?: string) => ({
  ...assetResponseQuery(assetId, viewer, versionId),
  select: (response: AssetResponse) => response.asset,
});

/** Resolve a readable page and prime the id cache used by editing and moderation. */
export const assetBySlugQuery = (
  kind: AssetKind,
  slug: string,
  viewer: AssetViewer,
  versionId?: string,
) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.bySlug.get.key(), { kind, slug, viewer, versionId }],
    queryFn: () =>
      reachable(publicApi.assets.bySlug.get({ params: { kind, slug }, query: { versionId } })),
    staleTime: 30_000,
  });

export function cacheAssetResponse(
  queryClient: QueryClient,
  viewer: AssetViewer,
  response: AssetResponse,
  versionId?: string,
) {
  queryClient.setQueryData(
    assetResponseQuery(response.asset.id, viewer, versionId).queryKey,
    response,
  );
}

/** The version's refusal, appeal, and removal, from the same response; only its editors get them. */
export const assetModerationQuery = (assetId: string, viewer: AssetViewer, versionId?: string) => ({
  ...assetResponseQuery(assetId, viewer, versionId),
  select: (response: AssetResponse) => response.moderation ?? null,
});

/**
 * Puts a changed version in its cached response, keeping the moderation state read with it, and in
 * the asset's own entry too when that's the version it shows.
 */
export function setCachedAsset(queryClient: QueryClient, viewer: AssetViewer, asset: Asset) {
  queryClient.setQueryData(
    assetResponseQuery(asset.id, viewer, asset.versionId).queryKey,
    current => (current ? { ...current, asset } : { asset }),
  );
  queryClient.setQueryData(assetResponseQuery(asset.id, viewer).queryKey, current =>
    current?.asset.versionId === asset.versionId ? { ...current, asset } : current,
  );
}

export const myAssetsQuery = (status: AssetListStatus | undefined, viewer: AssetViewer) =>
  infiniteQueryOptions({
    queryKey: [...publicOrpc.assets.list.key(), { status, viewer }],
    queryFn: ({ pageParam }) =>
      reachable(
        api.assets.list({
          query: {
            ownerId: viewer ?? undefined,
            limit: PAGE_SIZE,
            status,
            cursor: pageParam ?? undefined,
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: AssetPage) => lastPage.nextCursor,
    enabled: viewer !== null,
    staleTime: 10_000,
  });

/** An asset's published versions, newest first, and its drafts for its owner and collaborators. */
export const assetVersionsQuery = (assetId: string, viewer: AssetViewer) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.versions.list.key(), { assetId, viewer }],
    queryFn: async () =>
      (await reachable(publicApi.assets.versions.list({ params: { id: assetId } }))).versions,
    staleTime: 30_000,
  });

export const assetTagsQuery = (kind: AssetKind | undefined, prefix?: string) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.tags.list.key(), { kind, prefix }],
    queryFn: async () =>
      (await reachable(publicApi.assets.tags.list({ query: { kind, prefix, limit: 24 } }))).tags,
    staleTime: 5 * 60_000,
  });

/** Collections anyone can find: the largest first, or the titles that match what is typed. */
export const collectionsQuery = (
  filters: { q?: string; official?: boolean },
  viewer: AssetViewer,
) =>
  infiniteQueryOptions({
    queryKey: [
      ...(filters.q
        ? publicOrpc.assets.collections.search.key()
        : publicOrpc.assets.collections.list.key()),
      { filters, viewer },
    ],
    queryFn: ({ pageParam }) => {
      const query = {
        limit: PAGE_SIZE,
        official: filters.official === undefined ? undefined : (`${filters.official}` as const),
        cursor: pageParam ?? undefined,
      };
      return reachable(
        filters.q
          ? publicApi.assets.collections.search({ query: { ...query, q: filters.q } })
          : publicApi.assets.collections.list({ query }),
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: AssetCollectionPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

/**
 * The caller's own and shared collections: the most recently changed first,
 * or the titles that match what is typed.
 */
export const myCollectionsQuery = (filters: { q?: string }, viewer: AssetViewer) =>
  infiniteQueryOptions({
    queryKey: [
      ...(filters.q
        ? publicOrpc.assets.collections.search.key()
        : publicOrpc.assets.collections.list.key()),
      { filters, viewer },
    ],
    queryFn: ({ pageParam }) => {
      const query = { editorId: viewer ?? undefined, limit: 50, cursor: pageParam ?? undefined };
      return reachable(
        filters.q
          ? api.assets.collections.search({ query: { ...query, q: filters.q } })
          : api.assets.collections.list({ query: { ...query, sort: "recentlyChanged" } }),
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: AssetCollectionPage) => lastPage.nextCursor,
    enabled: viewer !== null,
    staleTime: 30_000,
  });

/** Get a collection's whole response, which carries the owner's moderation state beside it. */
const collectionResponseQuery = (collectionId: string, viewer: AssetViewer) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.collections.get.key(), { collectionId, viewer }],
    queryFn: () => reachable(publicApi.assets.collections.get({ params: { collectionId } })),
    staleTime: 30_000,
  });

export const collectionQuery = (collectionId: string, viewer: AssetViewer) => ({
  ...collectionResponseQuery(collectionId, viewer),
  select: (response: AssetCollectionResponse) => response.collection,
});

/** The collection's refusal, appeal, and removal, from the same response; only its owner gets them. */
export const collectionModerationQuery = (collectionId: string, viewer: AssetViewer) => ({
  ...collectionResponseQuery(collectionId, viewer),
  select: (response: AssetCollectionResponse) => response.moderation ?? null,
});

export const collectionAssetsQuery = (collectionId: string, viewer: AssetViewer) =>
  infiniteQueryOptions({
    queryKey: [...publicOrpc.assets.collections.assets.list.key(), { collectionId, viewer }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.assets.collections.assets.list({
          params: { collectionId },
          query: { limit: PAGE_SIZE, cursor: pageParam ?? undefined },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: AssetPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

export const collaboratorsQuery = (collectionId: string) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.collections.collaborators.list.key(), { collectionId }],
    queryFn: async () =>
      (await reachable(api.assets.collections.collaborators.list({ params: { collectionId } })))
        .collaborators,
  });

/**
 * The viewer's own and shared collections holding an asset: where they have
 * saved it. They are few however many public collections hold it, so one
 * page has them all.
 */
export const savedInQuery = (assetId: string, viewer: AssetViewer) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.collections.list.key(), { assetId, viewer }],
    queryFn: async () =>
      (
        await reachable(
          api.assets.collections.list({
            query: { assetId, editorId: viewer ?? undefined, limit: 50 },
          }),
        )
      ).collections,
    enabled: viewer !== null,
    staleTime: 30_000,
  });

/** The assets like one: the closest to it in meaning, and the ones whose files are most alike. */
export const similarAssetsQuery = (assetId: string, viewer: AssetViewer) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.similar.list.key(), { assetId, viewer }],
    queryFn: () => reachable(publicApi.assets.similar.list({ params: { id: assetId }, query: {} })),
    staleTime: 5 * 60_000,
  });

/** The published scenarios whose games have used an asset, the one with the most such games first. */
export const assetPresetsQuery = (assetId: string) =>
  queryOptions({
    queryKey: [...publicOrpc.assets.presets.list.key(), { assetId }],
    queryFn: async () =>
      (await reachable(publicApi.assets.presets.list({ params: { id: assetId } }))).presets,
    staleTime: 5 * 60_000,
  });

/**
 * The published copies of a draft, by the check publishing runs. They are
 * worked out from the draft as it is now, so `basis` names what it was
 * checked as: its file and its saved words.
 */
export const draftCopiesQuery = (draft: Asset, viewer: AssetViewer, basis: string) =>
  queryOptions({
    queryKey: [
      ...publicOrpc.assets.drafts.copies.list.key(),
      { versionId: draft.versionId, viewer, basis },
    ],
    queryFn: () =>
      reachable(
        api.assets.drafts.copies.list({ params: { id: draft.id, versionId: draft.versionId } }),
      ),
    staleTime: 5 * 60_000,
  });

/**
 * Every asset and collection read at once. A change to one asset moves its
 * place in the library, in a collection, and on the author's own shelf, so a
 * write refreshes them all rather than guessing which held it.
 */
export function refreshAssets(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: publicOrpc.assets.key() });
}

/**
 * What is already known about an asset before its own request answers.
 * Arriving from a grid means the browser already holds the picture and the
 * cache holds everything said about it, so the page opens as itself rather
 * than as a skeleton that later becomes the asset.
 */
export function cachedAsset(queryClient: QueryClient, assetId: string): Asset | undefined {
  for (const key of [
    publicOrpc.assets.list.key(),
    publicOrpc.assets.search.key(),
    publicOrpc.assets.collections.assets.list.key(),
  ]) {
    for (const [, data] of queryClient.getQueriesData<InfiniteData<AssetPage>>({ queryKey: key })) {
      for (const page of data?.pages ?? []) {
        const found = page.assets.find(asset => asset.id === assetId);
        if (found) return found;
      }
    }
  }
  for (const [, data] of queryClient.getQueriesData<SimilarAssets>({
    queryKey: publicOrpc.assets.similar.list.key(),
  })) {
    const similar = [...(data?.byMeaning ?? []), ...(data?.byMedia ?? [])];
    const found = similar.find(item => item.asset.id === assetId);
    if (found) return found.asset;
  }
  return undefined;
}
